import { supabaseClient } from "@/lib/supabase";

import { INPUT_UPLOADS_BUCKET, buildDocumentStoragePath, sanitizeDocumentFilename } from "./uploadDocument";

const DEFAULT_EXPIRES_IN_SECONDS = 300;

export async function getDiaryIdForEntry(entryId: string): Promise<string | null> {
    const { data, error } = await supabaseClient
        .from("entries")
        .select("diary_id")
        .eq("entry_id", entryId)
        .maybeSingle();

    if (error) {
        throw error;
    }

    return data?.diary_id ?? null;
}

async function createSignedUrlForPath(storagePath: string, expiresIn: number): Promise<string> {
    const { data, error } = await supabaseClient.storage
        .from(INPUT_UPLOADS_BUCKET)
        .createSignedUrl(storagePath, expiresIn);

    if (error || !data?.signedUrl) {
        throw error ?? new Error("Failed to create signed URL for an uploaded file.");
    }

    return data.signedUrl;
}

async function listInputUploadPath(params: {
    diaryId: string;
    entryId: string;
    inputId: string;
    fileName: string;
}): Promise<string | null> {
    const prefix = `${params.diaryId}/${params.entryId}/${params.inputId}`;
    const { data: files, error } = await supabaseClient.storage.from(INPUT_UPLOADS_BUCKET).list(prefix);

    if (error || !files?.length) {
        return null;
    }

    const sanitizedName = sanitizeDocumentFilename(params.fileName);
    const match = files.find((file) => file.name === sanitizedName) ?? files[0];

    return match ? `${prefix}/${match.name}` : null;
}

/**
 * Create a short-lived signed URL for a file input answer stored in entry_values.
 */
export async function getInputUploadSignedUrl(params: {
    diaryId: string;
    entryId: string;
    inputId: string;
    fileName: string;
    expiresIn?: number;
}): Promise<string> {
    const expiresIn = params.expiresIn ?? DEFAULT_EXPIRES_IN_SECONDS;
    const storagePath = buildDocumentStoragePath({
        diaryId: params.diaryId,
        entryId: params.entryId,
        inputId: params.inputId,
        filename: params.fileName,
    });

    try {
        return await createSignedUrlForPath(storagePath, expiresIn);
    } catch (primaryError) {
        const fallbackPath = await listInputUploadPath(params);

        if (!fallbackPath) {
            throw primaryError;
        }

        return createSignedUrlForPath(fallbackPath, expiresIn);
    }
}
